import { useQuery } from '@tanstack/react-query'
import { Cable, X } from 'lucide-react'
import { createContext, useContext, useEffect, useRef, type CSSProperties, type FormEvent, type ReactNode } from 'react'
import { api } from './api.ts'
import type { AppKind } from './types.ts'

export function Initial({ name, size = 40 }: { name: string; size?: number }) {
  return (
    <span className="initial" style={{ width: size, height: size }} aria-hidden="true">
      {name.trim().slice(0, 1).toUpperCase() || '?'}
    </span>
  )
}

export function CompanyMark({ name, logo, size = 40 }: { name: string; logo: string | null; size?: number }) {
  return logo ? <img className="logo" src={logo} alt="" width={size} height={size} /> : <Initial name={name} size={size} />
}

/** Marks an Icefield administration page, beside its title. */
export function AdminTag() {
  return <Badge tone="admin">Administrator</Badge>
}

export const KIND_LABEL: Record<AppKind, string> = { app: 'Apps', mcp: 'MCP Servers' }

/** An app's name as listed: an MCP server's ends in "MCP", so it isn't taken for an app. */
export function itemName({ name, kind }: { name: string; kind?: AppKind }): string {
  return kind === 'mcp' && !/\bMCP$/.test(name) ? `${name} MCP` : name
}

/** Apps and MCP servers apart, apps first, each in the order given. A kind with none is left
 *  out. */
export function byKind<T extends { kind: AppKind }>(items: T[]): Array<{ kind: AppKind; label: string; items: T[] }> {
  return (['app', 'mcp'] as const)
    .map((kind) => ({ kind, label: KIND_LABEL[kind], items: items.filter((i) => i.kind === kind) }))
    .filter((g) => g.items.length > 0)
}

/** A row of the apps given, each a small icon and its name. */
export function AppList({ apps }: { apps: Array<{ appId: string; name: string; kind?: AppKind }> }) {
  if (apps.length === 0) return <span className="muted">—</span>
  return (
    <span className="app-list">
      {apps.map((a) => (
        <span key={a.appId} className="app-chip">
          <AppMark appId={a.appId} name={a.name} kind={a.kind} size={18} />
          {itemName(a)}
        </span>
      ))}
    </span>
  )
}

/** Every app icon's address, by app id, read once for every AppMark on the page. Changing an
 *  icon on the apps page refreshes it. */
export const APP_ICONS_KEY = ['app-icons']

/** An app's icon, set on the apps page, or its initial when it has none. An MCP server's icon
 *  is the logo of what it connects to, or its initial, with a small connector badge, Lucide's
 *  Cable, over its bottom-right corner. Sizes are a custom property, not an inline width, so a
 *  stylesheet can still shrink them as it does an icon's image. */
export function AppMark({ appId, name, size = 40, kind = 'app' }: { appId: string; name: string; size?: number; kind?: AppKind }) {
  const icons = useQuery({
    queryKey: APP_ICONS_KEY,
    queryFn: async () => (await api.get<{ icons: Record<string, string> }>('/app-icons')).icons,
    staleTime: 5 * 60_000,
  })
  const logo = icons.data?.[appId]
  if (kind === 'mcp') {
    return (
      <span className="mcp-mark" style={{ '--mark-size': `${size}px` } as CSSProperties} aria-hidden="true">
        {logo ? <img className="mcp-logo" src={logo} alt="" /> : <span className="mcp-tile">{name.slice(0, 1)}</span>}
        <span className="mcp-badge">
          <Cable />
        </span>
      </span>
    )
  }
  return logo ? (
    <img className="app-logo" src={logo} alt="" width={size} height={size} />
  ) : (
    <span className="tile-mark app-initial" style={{ '--mark-size': `${size}px` } as CSSProperties} aria-hidden="true">
      {name.slice(0, 1)}
    </span>
  )
}

export function Notice({ kind = 'error', children }: { kind?: 'error' | 'info' | 'ok'; children: ReactNode }) {
  if (!children) return null
  return (
    <p className={`notice notice-${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </p>
  )
}

export function Loading() {
  return <p className="muted" role="status">Loading…</p>
}

const ModalEdited = createContext<() => void>(() => {})

/** For something in a Modal that changes what it would save without typing in a field (a
 *  Select all button): after calling it, a click outside no longer closes the dialog. */
export function useMarkModalEdited() {
  return useContext(ModalEdited)
}

/** A modal dialog, using the browser's own <dialog>. A click outside it, or Escape, closes
 *  it, but only while nothing in it has been edited: once a field has changed, only the
 *  close button (or finishing the form) does. A control that saves as soon as it changes
 *  says so with `data-autosave`, and doesn't count as an edit; nor does typing in a search
 *  box. */
export function Modal({
  title,
  open,
  onClose,
  wide = false,
  children,
}: {
  title: string
  open: boolean
  onClose: () => void
  /** For a table or a whole page's worth of settings. */
  wide?: boolean
  children: ReactNode
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const edited = useRef(false)
  // Where the press started: a drag from inside that ends outside isn't a click outside.
  const pressedOutside = useRef(false)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) {
      edited.current = false
      dialog.showModal()
      // showModal() focuses the first focusable thing, the close button. A form starts at
      // its first field instead.
      dialog.querySelector<HTMLElement>('input:not([type="hidden"]):not([type="file"]):not([data-autosave]), select, textarea')?.focus()
    }
    if (!open && dialog.open) dialog.close()
  }, [open])

  const outside = (e: { clientX: number; clientY: number }) => {
    const r = ref.current!.getBoundingClientRect()
    return e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom
  }
  const noteEdit = (e: FormEvent<HTMLDialogElement>) => {
    if (!(e.target as HTMLElement).closest('[data-autosave], [role="search"]')) edited.current = true
  }

  return (
    <dialog
      ref={ref}
      className={wide ? 'modal-wide' : undefined}
      onClose={onClose}
      aria-labelledby="modal-title"
      onInput={noteEdit}
      onChange={noteEdit}
      onMouseDown={(e) => {
        pressedOutside.current = e.target === ref.current && outside(e)
      }}
      onClick={(e) => {
        if (pressedOutside.current && e.target === ref.current && outside(e) && !edited.current) onClose()
        pressedOutside.current = false
      }}
      onCancel={(e) => {
        if (edited.current) e.preventDefault()
      }}
    >
      <div className="modal-head">
        <h2 id="modal-title">{title}</h2>
        <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
          <X aria-hidden="true" />
        </button>
      </div>
      <ModalEdited.Provider value={() => (edited.current = true)}>{open && children}</ModalEdited.Provider>
    </dialog>
  )
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  )
}

const dateTime = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' })

export function when(iso: string | null): string {
  return iso ? dateTime.format(new Date(iso)) : '—'
}

export function Badge({ tone = 'neutral', children }: { tone?: 'neutral' | 'ok' | 'warn' | 'bad' | 'accent' | 'admin'; children: ReactNode }) {
  const text = typeof children === 'string' ? children.charAt(0).toUpperCase() + children.slice(1) : children
  return <span className={`badge badge-${tone}`}>{text}</span>
}
